import React from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { postAddWidget } from '../../../utilityFunctions/dashboardApi';

const MAX_DESCRIPTION = 2000;

/**
 * "Add widget" from a natural-language description: POST /{id}/widgets → { run_id }. The run is an
 * NL edit (same RunProgressPanel, same EDIT_PLAN review), so the parent just mounts the panel.
 * 409 → onStale(currentVersionId); other errors stay inline with the text kept.
 *
 * @param onRunStarted(runId)
 * @param onStale(currentVersionId)
 */
export default function AddWidgetDialog({ open, onClose, dashboardId, baseVersionId, jwt, disabled, onRunStarted, onStale }) {
  const { t } = useTranslation();
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => { if (open) setError(null); }, [open]);

  const submit = async () => {
    const value = text.trim();
    if (!value || busy || disabled || !baseVersionId) return;
    setBusy(true);
    setError(null);
    const res = await postAddWidget(dashboardId, baseVersionId, value, jwt);
    setBusy(false);
    if (res.ok && res.data && res.data.run_id != null) {
      setText('');
      onRunStarted(res.data.run_id);
      return;
    }
    if (res.status === 409) {
      onStale(res.data && res.data.current_version_id);
      return;
    }
    setError((res.data && (res.data.message || res.data.error)) || t('Dashboards.addWidget.failed'));
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { minWidth: 0, maxWidth: { xs: 'calc(100% - 16px)', sm: 600 }, m: { xs: 1, sm: 4 } } }}>
      <DialogTitle>{t('Dashboards.addWidget.title')}</DialogTitle>
      <DialogContent sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t('Dashboards.addWidget.intro')}</Typography>
        <TextField
          value={text}
          onChange={(e) => setText(e.target.value)}
          multiline
          minRows={3}
          maxRows={8}
          fullWidth
          autoFocus
          disabled={busy || disabled}
          label={t('Dashboards.addWidget.label')}
          placeholder={t('Dashboards.addWidget.placeholder')}
          inputProps={{ 'aria-label': t('Dashboards.addWidget.label'), maxLength: MAX_DESCRIPTION }}
        />
        {error && <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={busy}>{t('Dashboards.common.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={busy || disabled || !text.trim()}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AddIcon />} sx={{ textTransform: 'none' }}>
          {t('Dashboards.addWidget.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
